import apiBase, { ApiBase } from './apiBase';
import dataUriToBlob from '../utils/dataUriToBlob';
import blobApiBase from './blobApiBase';

function createApplicationFormData(data) {
  const formData = new FormData();
  formData.append('title', data.title ? data.title : '');
  if (data.district) formData.append('district', data.district);
  if (data.category) formData.append('category', data.category);
  if (data.subcategory) formData.append('subcategory', data.subcategory);
  if (data.reason) formData.append('reason', data.reason);

  if (data.costs) {
    for (let i = 0; i < data.costs.length; i++) {
      formData.append(`costs[${i}].name`, data.costs[i].name);
      if (data.costs[i].unit != null) formData.append(`costs[${i}].unit`, data.costs[i].unit);
      if (data.costs[i].price != null) formData.append(`costs[${i}].price`, data.costs[i].price);
      if (data.costs[i].qty != null) formData.append(`costs[${i}].qty`, data.costs[i].qty);
      formData.append(`costs[${i}].value`, data.costs[i].value);
    }
  }

  if (data.locations) {
    for (let i = 0; i < data.locations.length; i++) {
      if (data.locations[i].address) formData.append(`locations[${i}].address`, data.locations[i].address);
      if (data.locations[i].city) formData.append(`locations[${i}].city`, data.locations[i].city);
      if (data.locations[i].description) formData.append(`locations[${i}].description`, data.locations[i].description);
      if (data.locations[i].plotNumber) formData.append(`locations[${i}].plotNumber`, data.locations[i].plotNumber);
      if (data.locations[i].lat) formData.append(`locations[${i}].lat`, data.locations[i].lat);
      if (data.locations[i].lng) formData.append(`locations[${i}].lng`, data.locations[i].lng);
    }
  }

  if (data.form) {
    for (let i = 0; i < data.form.length; i++) {
      formData.append(`form[${i}].id`, data.form[i].id);
      if (data.form[i].value) formData.append(`form[${i}].value`, data.form[i].value);
    }
  }

  if (data.attachment) {
    if (data.attachment.data) {
      formData.append('attachment', dataUriToBlob(data.attachment.data), data.attachment.name);
    } else {
      if (data.attachment.id) formData.append('savedListOfSupportAttachment.id', data.attachment.id);
      if (data.attachment.fileName) formData.append('savedListOfSupportAttachment.fileName', data.attachment.fileName);
    }
  }

  if (data.voter) {
    Object.keys(data.voter).forEach(k => {
      if (data.voter[k]) {
        if (typeof data.voter[k] === 'object') {
          formData.append(`voter.${k}`, data.voter[k].toISOString());
        } else {
          formData.append(`voter.${k}`, data.voter[k]);
        }
      }
    });
  }

  if (data.attachments && data.attachments.length > 0) {
    data.attachments.filter(a => a && a.data).map((attachment) => {
      formData.append('attachments', dataUriToBlob(attachment.data), attachment.name);
    });
    const attachementsWithoutData = data.attachments.filter(a => a && !a.data);
    for (let i = 0; i < attachementsWithoutData.length; i++) {
      if (attachementsWithoutData[i].id) formData.append(`savedAttachments[${i}].id`, attachementsWithoutData[i].id);
      if (attachementsWithoutData[i].fileName) formData.append(`savedAttachments[${i}].fileName`, attachementsWithoutData[i].fileName);
    }
  }
  return formData;
}

class BudgetApi {
  static getCurrent() {
    return apiBase.get('/budget/current');
  }

  static getApplicationDetails(applicationId) {
    return apiBase.get(`/public/budget/applications/${applicationId}`);
  }

  static getMyApplications() {
    return apiBase.get(`/budget/myapplications`);
  }

  static getBudgetWithMyApplication(applicationId) {
    return apiBase.get(`/budget/myapplications/${applicationId}`);
  }

  static loadVoteFormSettings(budgetId) {
    return apiBase.get(`/public/budget/vote?budgetId=${budgetId}`);
  }

  static submitApplication(budgetId, data) {
    const config = { headers: { 'content-type': 'multipart/form-data' } };
    return apiBase.post(`/budget/${budgetId}/submitApplication`, createApplicationFormData(data), config);
  }

  static submitApplicationSupport(budgetId, applicationId, data) {
    return apiBase.post(`/public/budget/${budgetId}/applications/${applicationId}/support/submit`, data);
  }

  static createDraftApplication(budgetId, data) {
    const config = { headers: { 'content-type': 'multipart/form-data' } };
    return apiBase.post(`/budget/${budgetId}/createDraftApplication/`, createApplicationFormData(data), config);
  }

  static updateDraftApplication(budgetId, applicationId, data) {
    const config = { headers: { 'content-type': 'multipart/form-data' } };
    return apiBase.put(`/budget/${budgetId}/updateDraftApplication/${applicationId}`, createApplicationFormData(data), config);
  }

  static submitDraftApplication(budgetId, applicationId, data) {
    const config = { headers: { 'content-type': 'multipart/form-data' } };
    return apiBase.put(`/budget/${budgetId}/submitDraftApplication/${applicationId}`, createApplicationFormData(data), config);
  }

  static submitApplicationChanges(budgetId, applicationId, data) {
    const config = { headers: { 'content-type': 'multipart/form-data' } };
    return apiBase.post(`/budget/${budgetId}/myapplications/${applicationId}/submit-changes`, createApplicationFormData(data), config);
  }

  static submitManualApplication(budgetId, data, token) {
    const config = { headers: { 'content-type': 'multipart/form-data' } };
    return ApiBase.createWithToken(token).post(`/budget/${budgetId}/createApplication`, createApplicationFormData(data), config);
  }

  static vote(data) {
    return apiBase.post('/public/budget/vote', data);
  }

  static submitDraftVote(data) {
    return apiBase.post('/public/budget/votes/drafts', data);
  }

  static getMyVotes() {
    return apiBase.get('/public/budget/votes/my');
  }

  static getVoteDetails(id) {
    return apiBase.get(`/public/budget/votes/drafts/${id}`);
  }

  static voteManual(id, data) {
    return apiBase.post(`/budget/${id}/createVote`, data);
  }

  static startPhoneNumberVerification(budgetId, phoneNumber, verificationMethodId) {
    return apiBase.post(`/public/budget/${budgetId}/start-phone-number-verification`, { phoneNumber, verificationMethodId });
  }

  static suggestVoters(budgetId, data, methodId) {
    return apiBase.post(`/budget/${budgetId}/suggest-voters/${methodId}`, data);
  }

  static getReport(budgetId, applicationId, params) {
    return blobApiBase.post(`/budget/${budgetId}/applications/${applicationId}/participantReport`, params);
  }
}

export default BudgetApi;



// WEBPACK FOOTER //
// ./src/api/budgetApi.js